import { useCallback, useEffect, useState } from "react"
import merge from "lodash/merge"
import { useAppDispatch, useAppSelector } from "../../hooks/reduxToolkit"
import { useAsyncAction } from "../../hooks/useAsyncAction"
import { useAsyncEffect } from "../../hooks/useAsyncEffect"
import { useLoadSettingsFromServer } from "../../hooks/useLoadSettingsFromServer"
import { useClient } from "../../providers/supabase"
import { useBackendApi } from "../../hooks/useBackendApi"
import { updateMySettings } from "@healthshare/shared/commands/account"
import { updateMyMembership } from "@healthshare/shared/commands/memberships"
import { usePushNotifications } from "../../providers/pushNotifications"
import { useSentry } from "../../providers/sentry"
import { setAliceHealthDataConsent, setSettings } from "../../reduxToolkit/membershipSlice"
import type { MembershipUpdate, SettingsRow, SettingsUpdate } from "../../../types/supabase"
import type { NotificationPermission } from "../../../lib/push/interface"

export interface AliceContactSettingsValue {
  /** Alice may notify the member in the app / by push. `settings.notification_prefs_push` */
  inApp: boolean
  /** Alice may email the member. `settings.notification_prefs_email` */
  email: boolean
  /**
   * Alice may put health information in those emails.
   * `memberships.alice_health_data_consent_at`.
   *
   * Email only — that flag gates `Alice::repliesOverEmail()` runs and nothing
   * else. Push carries no content to consent to, and provider correspondence is
   * authorized per-provider by `communication_authorized_at`.
   *
   * Unlike the two channel toggles, which are this user's own settings row, this
   * one is shared by everyone on the membership. See `save()` for why that means
   * this screen grants but never revokes.
   */
  emailHealthDataConsent: boolean
}

/**
 * Reads the current channel preferences, defaulting both channels on.
 *
 * `notification_prefs_push` is nullable with no database default, so null there
 * means "never asked", not "declined" — the same reason `?? true` is right for
 * email on a row created before that column existed.
 */
function fromServer(
  settings: SettingsRow | null | undefined,
  consentedAt: string | null | undefined
): AliceContactSettingsValue {
  return {
    inApp: settings?.notification_prefs_push ?? true,
    email: settings?.notification_prefs_email ?? true,
    emailHealthDataConsent: !!consentedAt,
  }
}

/**
 * Owns the state behind `<AliceContactSettings />` so the form around it can gate
 * its submit button on `isValid` and persist with `save()` alongside its own
 * fields. Kept separate from the component for exactly that reason.
 */
export function useAliceContactSettings() {
  const client = useClient()
  const api = useBackendApi()
  const dispatch = useAppDispatch()
  const pushNotifications = usePushNotifications()
  const { captureException } = useSentry()

  const settings = useAppSelector((s) => s.membership?.settings)
  const membershipId = useAppSelector((s) => s.membership?.membershipId)
  const consentedAt = useAppSelector((s) => s.membership?.aliceHealthDataConsentAt)

  useLoadSettingsFromServer()

  // Consent can also be given (or withdrawn) by talking to Alice, so the persisted
  // slice value may predate that. Re-read it when the screen opens rather than
  // showing the member a checkbox that disagrees with what Alice was told.
  useAsyncEffect(async () => {
    if (!membershipId) { return }

    const result = await client.from('memberships')
      .select('alice_health_data_consent_at')
      .eq('id', membershipId)
      .maybeSingle()

    if (result.error) { throw result.error }

    dispatch(setAliceHealthDataConsent({
      consentedAt: result.data?.alice_health_data_consent_at ?? null,
    }))
  }, [client, dispatch, membershipId])

  const [value, setValue] = useState<AliceContactSettingsValue>(() => fromServer(settings, consentedAt))
  const [touched, setTouched] = useState(false)

  // Settings and consent land asynchronously (see the two hooks above). Adopt them
  // until the member starts editing, after which their choices win.
  useEffect(() => {
    if (touched) { return }
    setValue(fromServer(settings, consentedAt))
  }, [settings, consentedAt, touched])

  const [permission, setPermission] = useState<NotificationPermission>(
    () => pushNotifications.getPermissionState())
  useEffect(() => {
    setPermission(pushNotifications.getPermissionState())
  }, [pushNotifications])

  const [pushError, setPushError] = useState<Error | null>(null)

  const enablePush = useCallback(async () => {
    setPushError(null)
    try {
      const result = await pushNotifications.register()
      setPermission(result || pushNotifications.getPermissionState())
    } catch (e) {
      captureException(e, { tags: { feature: 'notifications', notification_op: 'register-push' } })
      setPushError(e as Error)
    }
  }, [pushNotifications, captureException])

  const update = useCallback((patch: Partial<AliceContactSettingsValue>) => {
    setTouched(true)
    setValue((v) => ({ ...v, ...patch }))
  }, [])

  // The "enable push notifications" button is only a gate while pressing it could
  // still change something. On a plain desktop browser (`not-supported`) or once
  // the OS has blocked us (`denied`) no amount of pressing grants permission, so
  // requiring it there would strand the member on this screen.
  const pushSatisfied = permission !== 'default'

  const isValid =
    (!value.email || value.emailHealthDataConsent) &&
    (!value.inApp || pushSatisfied)

  const [saveState, save] = useAsyncAction(async () => {
    const now = new Date().toISOString()

    const settingsUpdate: SettingsUpdate = {
      notification_prefs_push: value.inApp,
      notification_prefs_email: value.email,
    }
    const saved = await updateMySettings(api, settingsUpdate)

    dispatch(setSettings({ settings: merge({}, settings, saved) as SettingsRow }))

    // Health-data consent lives on the membership rather than the settings row —
    // it is a property of the membership whose data Alice would be handling, and
    // the same flag can be set by telling Alice directly in a conversation. Every
    // change is logged to `membership_consent_events` by a database trigger.
    //
    // This screen only ever GRANTS. `notification_prefs_email` above is this user's
    // own settings row, but the consent flag is shared by everyone on the
    // membership: a member switching their own email off must not clear a grant
    // their spouse gave — possibly in a conversation with Alice, and while that
    // spouse still has email on. Withdrawal belongs where it can be aimed
    // deliberately at the household flag, which is the Privacy Settings toggle.
    const granting = value.email && value.emailHealthDataConsent && !consentedAt
    if (granting) {
      if (!membershipId) { throw new Error('No membership to record consent against') }

      const update: MembershipUpdate = {
        alice_health_data_consent_at: now,
        alice_health_data_consent_source: 'web-settings',
        // Only consent captured in a conversation cites a message; the database
        // rejects a message id paired with any other source.
        alice_health_data_consent_message_id: null,
      }

      await updateMyMembership(api, membershipId, update)

      dispatch(setAliceHealthDataConsent({ consentedAt: now }))
    }

    setTouched(false)
  }, [api, dispatch, membershipId, settings, consentedAt, value])

  return {
    value,
    update,
    isValid,
    save,
    saveError: saveState.error,
    permission,
    pushSatisfied,
    enablePush,
    pushError,
  }
}

export type AliceContactSettingsProps = ReturnType<typeof useAliceContactSettings>

/**
 * "How can Alice reach you?" — the notification channels, framed as what the
 * member is letting Alice do, plus the consent each channel needs before it can
 * carry anything.
 */
export function AliceContactSettings({
  value,
  update,
  permission,
  pushSatisfied,
  enablePush,
  pushError,
  saveError,
}: AliceContactSettingsProps) {
  return <div className="alice-contact-settings col-12 mt-4">
    <hr />
    <h4>How can Alice reach you?</h4>
    <p className="text-muted">
      Alice is our AI assistant. She'll let you know when a bill needs your attention,
      when she's heard back from a provider, and when something is due.
    </p>

    <div className="form-group">
      <div className="form-check form-switch">
        <input type="checkbox" className="form-check-input"
          id="alice-contact-in-app"
          checked={value.inApp}
          onChange={(e) => update({ inApp: e.target.checked })} />
        <label className="form-check-label" htmlFor="alice-contact-in-app">
          In the app
          <small className="text-muted d-block">
            A notification on your phone and a message in the app.
          </small>
        </label>
      </div>

      {value.inApp && <div className="ms-4 mt-2">
        {permission === 'granted' && <div className="text-success">
          <i className="material-icons align-middle">check_circle</i>
          &nbsp;Push notifications are enabled.
        </div>}

        {permission === 'default' && <>
          <button type="button" className="btn btn-outline-primary"
            onClick={() => enablePush()}>
            Enable push notifications
          </button>
          <small className="form-text text-muted d-block">
            Required — your phone has to let us send them before Alice can.
          </small>
        </>}

        {permission === 'denied' && <div className="text-danger">
          Notifications are blocked. Please enable them in your phone or browser
          settings, then come back to this screen.
        </div>}

        {permission === 'not-supported' && <div className="text-muted">
          This browser can't receive push notifications. Alice will still reach you
          inside the app — install it on your phone to get them there too.
        </div>}

        {pushError && <div className="text-danger">{pushError.message}</div>}
      </div>}
    </div>

    <div className="form-group mt-3">
      <div className="form-check form-switch">
        <input type="checkbox" className="form-check-input"
          id="alice-contact-email"
          checked={value.email}
          onChange={(e) => update({ email: e.target.checked })} />
        <label className="form-check-label" htmlFor="alice-contact-email">
          By email
          <small className="text-muted d-block">
            Sent to your sign-in email address.
          </small>
        </label>
      </div>

      {value.email && <div className="ms-4 mt-2">
        <div className="form-check">
          <input type="checkbox" className="form-check-input"
            id="alice-email-health-data-consent"
            required
            checked={value.emailHealthDataConsent}
            onChange={(e) => update({ emailHealthDataConsent: e.target.checked })} />
          <label className="form-check-label" htmlFor="alice-email-health-data-consent">
            I agree to receive health information by email
            <small className="text-muted d-block">
              To be useful over email, Alice has to name the things she's writing about:
              your medical expenses, the providers you saw, your bills and the conditions
              they were for. Email isn't a secure channel — messages can be read by anyone
              who has access to your inbox, can sometimes sit unencrypted on mail servers along the
              way, and can go to the wrong person if the address is mistyped. If you'd
              rather she didn't, turn email off above. Once given, this covers everyone on
              your membership; withdraw it any time under Privacy Settings.
            </small>
          </label>
        </div>
        {!value.emailHealthDataConsent && <small className="form-text text-danger d-block ms-4">
          Required while email is turned on.
        </small>}
      </div>}
    </div>

    {saveError && <div className="alert alert-danger mt-2" role="alert">
      {saveError.message}
    </div>}
  </div>
}
